import { fabric } from 'fabric';
import { cloneDeep } from 'lodash';
import { clearClipboard, isEmpty, getMinPositionObject } from '@/common/utils';
import { OBJECT_TYPE } from '@/common/constants';
import { COPY_OBJECT_KEY } from '@/common/constants/config';
import { computePastedObjectCoord } from './pasteObject';
import { parsePasteObject } from '@/common/utils/string';
import { getUniqueId } from './util';

const getChildObjectInGroup = (currentObjects, object) => {
  if (object.type === OBJECT_TYPE.MULTI_OBJECT) {
    const groupObj = {
      ...currentObjects[object.id],
      id: null
    };
    const childObjects = [];
    groupObj.objects.forEach(childObjId => {
      if (currentObjects[childObjId]?.type === OBJECT_TYPE.MULTI_OBJECT) {
        childObjects.push(
          getChildObjectInGroup(currentObjects, currentObjects[childObjId])
        );
      } else {
        childObjects.push({
          ...currentObjects[childObjId],
          id: null
        });
      }
    });
    groupObj.childObjects = childObjects;
    return groupObj;
  } else {
    return {
      ...currentObjects[object.id],
      id: null
    };
  }
};

/**
 * Handle copy object and save session storage
 *
 * @param   {Object}  event event's clipboard
 * @param   {Array}  currentObjects current objects
 * @param   {Object}  pageSelected page selected
 * @param   {Object}  canvas print canvas or digital canvas
 */
export const copyPpObject = (event, currentObjects, pageSelected, canvas) => {
  let activeObj = canvas.getActiveObject();

  if (!activeObj) return;

  if (event?.clipboardData) {
    clearClipboard(event);
  }

  const activeObjClone = cloneDeep(activeObj);
  let objects = [activeObjClone];

  if (activeObjClone.getObjects) {
    const specialObject = [
      OBJECT_TYPE.CLIP_ART,
      OBJECT_TYPE.TEXT,
      OBJECT_TYPE.MULTI_OBJECT
    ].includes(activeObjClone.objectType);
    objects = specialObject
      ? [activeObjClone]
      : [...activeObjClone.getObjects()];
    if (activeObjClone.objectType !== OBJECT_TYPE.MULTI_OBJECT) {
      activeObjClone.ungroupOnCanvas();
    }
  }
  activeObjClone.objectType = activeObj.objectType;

  const jsonData = objects.map(obj => {
    if (obj.objectType === OBJECT_TYPE.MULTI_OBJECT) {
      return getChildObjectInGroup(currentObjects, currentObjects[obj.id]);
    } else {
      return {
        ...currentObjects[obj.id],
        id: null
      };
    }
  });
  const cacheData = {
    sheetId: pageSelected.id,
    fabric: {
      ...JSON.parse(JSON.stringify(activeObjClone)),
      objectType: activeObjClone.objectType
    },
    [COPY_OBJECT_KEY]: jsonData
  };
  sessionStorage.setItem(COPY_OBJECT_KEY, JSON.stringify(cacheData));
};
/**
 * Function handle active selection of object(s) pasted (single | multiplesingle)
 * @param {Array} listPastedObjects - List object(s) pasted
 * @param {Ref} canvas print canvas or digital canvas
 */
export const setObjectPastetActiveSelection = (listPastedObjects, canvas) => {
  if (listPastedObjects.length === 1) {
    canvas.setActiveObject(listPastedObjects[0]);
  } else if (listPastedObjects.length > 1) {
    const sel = new fabric.ActiveSelection(listPastedObjects, {
      canvas
    });
    canvas.setActiveObject(sel);
  }
};

export const handlePasteSingleObject = async ({
  sheetId,
  fabricObject,
  minLeft,
  minTop,
  pageSelected,
  countPaste,
  createElementFromPpData,
  isDigital,
  objectRender
}) => {
  const obj = cloneDeep(objectRender);

  const coord = computePastedObjectCoord({
    data: obj,
    sheetId,
    fabricObject,
    minLeft,
    minTop,
    pageSelected,
    countPaste,
    isDigital
  });
  if (!coord) return;

  const newData = {
    ...obj,
    id: getUniqueId(),
    coord
  };
  return createElementFromPpData(newData);
};

export const handlePasteGroupObject = async ({
  groupObj,
  sheetId,
  fabricObject,
  minLeft,
  minTop,
  pageSelected,
  countPaste,
  createElementFromPpData,
  isDigital,
  createGroupForCopyPaste,
  canvas
}) => {
  const newData = {
    ...groupObj,
    objects: [],
    id: getUniqueId()
  };
  for (const object of newData.childObjects) {
    if (object.type === OBJECT_TYPE.MULTI_OBJECT) {
      const groupNewObj = await handlePasteGroupObject({
        groupObj: object,
        sheetId,
        fabricObject,
        minLeft,
        minTop,
        pageSelected,
        countPaste,
        createElementFromPpData,
        isDigital,
        createGroupForCopyPaste,
        canvas
      });
      canvas.add(groupNewObj);
      newData.objects.push(groupNewObj.id);
    } else {
      const singleObj = await handlePasteSingleObject({
        sheetId,
        fabricObject,
        minLeft,
        minTop,
        pageSelected,
        countPaste,
        createElementFromPpData,
        isDigital,
        objectRender: object
      });
      canvas.add(singleObj);
      newData.objects.push(singleObj.id);
    }
  }
  return await createGroupForCopyPaste(newData);
};
/**
 * Funtion recursive handle create object(s) and add to store through data be copied and return list object(s) processed
 * @param {Array} objects - List object(s) copied
 * @param {Number} sheetId - Current sheet id
 * @param {Object} fabricObject Fabric's data
 * @param {Number} minLeft Min left position of list objects
 * @param {Number} minTop Min top position of list objects
 * @param {Object} pageSelected page selected
 * @param {Number} countPaste count paste of canvas
 * @param {Function} createElementFromPpData create element from ppData
 * @param {Boolean} isDigital is digital canvas or print canvas
 * @returns {Arrray} List object(s) pasted
 */
export const handlePasteItems = async ({
  objects,
  sheetId,
  fabricObject,
  minLeft,
  minTop,
  pageSelected,
  countPaste,
  createElementFromPpData,
  isDigital,
  createGroupForCopyPaste,
  canvas
}) => {
  const listPromise = [];

  objects.forEach(o => {
    if (o.type === OBJECT_TYPE.MULTI_OBJECT) {
      listPromise.push(
        handlePasteGroupObject({
          groupObj: o,
          sheetId,
          fabricObject,
          minLeft,
          minTop,
          pageSelected,
          countPaste,
          createElementFromPpData,
          isDigital,
          createGroupForCopyPaste,
          canvas
        })
      );
    } else {
      listPromise.push(
        handlePasteSingleObject({
          sheetId,
          fabricObject,
          minLeft,
          minTop,
          pageSelected,
          countPaste,
          createElementFromPpData,
          isDigital,
          objectRender: o
        })
      );
    }
  });
  return Promise.all(listPromise);
};
/**
 * Function handle to get object(s) be copied from clipboard when user press Ctrl + V (Windows), Command + V (macOS), or from action menu
 * @param {Object}  event event's clipboard
 * @param {Object} pageSelected page selected
 * @param {Number} countPaste count paste of canvas
 * @param {Function} createElementFromPpData create element from ppData
 * @param {Function} setProcessingPaste set processing paste
 * @param {Ref} canvas print canvas or digital canvas
 * @param {Boolean} isDigital is digital canvas or print canvas
 */
export const pastePpObject = async ({
  event,
  pageSelected,
  countPaste,
  createElementFromPpData,
  setProcessingPaste,
  canvas,
  isDigital,
  createGroupForCopyPaste
}) => {
  const objectCopy = sessionStorage.getItem(COPY_OBJECT_KEY);
  const objects = parsePasteObject(objectCopy);

  if (isEmpty(objects)) return;

  let dataCopyOutside = (
    event?.clipboardData || window?.clipboardData
  )?.getData('text');

  if (dataCopyOutside) {
    setProcessingPaste();
    return;
  }
  const { sheetId, fabric: savedFabric } = JSON.parse(objectCopy);

  canvas.discardActiveObject();

  const { minLeft, minTop } = getMinPositionObject(savedFabric);

  const listPastedObjects = await handlePasteItems({
    objects,
    sheetId,
    fabricObject: savedFabric,
    minLeft,
    minTop,
    pageSelected,
    countPaste,
    createElementFromPpData,
    isDigital,
    createGroupForCopyPaste,
    canvas
  });

  if (!listPastedObjects[0]) return;

  canvas.add(...listPastedObjects);

  setObjectPastetActiveSelection(listPastedObjects, canvas);
};
